<template>
  <div class="space-y-4">
    <div v-if="sede" class="flex justify-end font-medium text-gray-900">
      Actuando como CAJERO en delegación: {{ sede.descripcion }}
    </div>
    <div class="space-y-2 divide-y rounded-md bg-white shadow-md">
      <div class="px-6 py-4">
        <div class="w-full space-y-2">
          <form @submit.prevent="buscarComprobante">
            <div class="flex flex-col items-center gap-x-6 gap-y-2 sm:flex-row">
              <label
                for="nro-comprobante"
                class="whitespace-nowrap text-base font-medium text-gray-700"
                >Nro. comprobante
              </label>
              <input
                id="nro-comprobante"
                class="w-72 appearance-none rounded-md border border-gray-400 p-2 text-gray-900 placeholder-gray-700 transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 sm:text-sm"
                v-model="numeroComprobante"
              />
              <button
                class="w-fit space-x-2 whitespace-nowrap rounded-md border border-blue-brand-700 bg-blue-brand-500 px-3 py-2 text-sm font-medium text-blue-brand-100 transition duration-150 ease-in-out hover:bg-blue-brand-700 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 active:bg-blue-brand-700"
                type="submit"
              >
                Buscar
              </button>
            </div>
          </form>
        </div>
      </div>
      <transition
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <Cargando class="py-10" v-if="isLoading" />
        <Empty v-else-if="error" />
        <div class="px-6 py-4" v-else-if="comprobanteTerceros">
          <div class="w-full space-y-8">
            <MessageCard
              v-if="cantEstadosAnulados >= 2"
              type="warning"
              message="Se detectó que el comprobante fue usado y anulado en reiteradas ocasiones."
            />
            <div class="space-y-4">
              <div class="space-y-2">
                <div class="text-lg font-semibold text-gray-900">Datos del comprobante</div>
                <div class="flex flex-wrap gap-y-1 tracking-wide text-gray-700">
                  <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                    <span class="font-semibold">Nro. comprobante</span>
                    <span>{{ comprobante.numero_comprobante }}</span>
                  </div>
                  <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                    <span class="font-semibold">Monto</span>
                    <span>{{ comprobante.monto | currency }}</span>
                  </div>
                  <div v-if="comprobanteTerceros.sede" class="space-x-2">
                    <span class="font-semibold">Delegación</span>
                    <span class="whitespace-nowrap">{{
                      comprobanteTerceros.sede.descripcion
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="space-y-2">
                <div class="text-lg font-semibold text-gray-900">Datos del titular</div>
                <div class="flex flex-wrap gap-y-1 tracking-wide text-gray-700">
                  <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                    <span class="font-semibold">CUIT/CUIL</span>
                    <span>{{ comprobanteTerceros.cuit_cuil | cuit }}</span>
                  </div>
                  <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                    <span class="font-semibold">Apellido y nombre</span>
                    <span
                      >{{ comprobanteTerceros.apellido }}, {{ comprobanteTerceros.nombres }}</span
                    >
                  </div>
                  <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                    <span class="font-semibold">E-mail</span>
                    <span>{{ comprobanteTerceros.email }}</span>
                  </div>
                  <div class="space-x-2">
                    <span class="font-semibold">Teléfono</span>
                    <span>{{ comprobanteTerceros.telefono }}</span>
                  </div>
                </div>
              </div>
              <div class="space-y-2">
                <div class="text-lg font-semibold text-gray-900">Datos de quien abona</div>
                <div class="flex flex-wrap gap-y-1 tracking-wide text-gray-700">
                  <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                    <span class="font-semibold">CUIT/CUIL</span>
                    <span>{{ comprobanteTerceros.cuit_cuil_pago | cuit }}</span>
                  </div>
                  <div class="space-x-2">
                    <span class="font-semibold">Código de pago electrónico</span>
                    <span>{{ comprobante.codigo_pago_electronico }}</span>
                  </div>
                </div>
              </div>
              <div class="flex flex-wrap gap-x-10 gap-y-4">
                <div class="min-w-1/4 max-w-full gap-2 sm:max-w-[45%]">
                  <div class="text-lg font-semibold text-gray-900">Datos del pago</div>
                  <div class="space-x-2">
                    <span class="font-semibold">Estado</span>
                    <span class="uppercase">{{
                      textoEstadoPago(comprobanteTerceros.pago.estado_id)
                    }}</span>
                  </div>
                  <div class="" v-if="comprobanteTerceros.pago">
                    <span class="font-semibold">Observaciones</span>
                    <p v-if="comprobanteTerceros.pago.observaciones">
                      {{ comprobanteTerceros.pago.observaciones }}
                    </p>
                    <p v-else>&ndash;</p>
                  </div>
                </div>
                <div class="min-w-1/4 max-w-full gap-2 sm:max-w-[45%]">
                  <div class="text-lg font-semibold text-gray-900">Datos del uso</div>
                  <div class="space-x-2">
                    <span class="font-semibold">Estado</span>
                    <span class="uppercase">{{
                      textoEstadoUso(comprobanteTerceros.uso.estado_id)
                    }}</span>
                  </div>
                  <div class="" v-if="comprobanteTerceros.uso">
                    <span class="font-semibold">Observaciones</span>
                    <p v-if="comprobanteTerceros.uso.observaciones">
                      {{ comprobanteTerceros.uso.observaciones }}
                    </p>
                    <p v-else>&ndash;</p>
                  </div>
                </div>
              </div>
            </div>
            <InfoComprobanteUsado
              v-if="comprobanteUsado"
              :comprobanteTerceros="comprobanteTerceros"
              @reload="buscarComprobante"
            />
            <div v-else>
              <MarcarUsoComprobante
                v-if="mismaSede && !isEstadoPago('DEVUELTO')"
                :comprobanteTerceros="comprobanteTerceros"
                @reload="buscarComprobante"
              />
              <div v-if="!mismaSede" class="font-medium text-red-500">
                El comprobante no pertenece a su delegación.
              </div>
              <MessageCard
                v-else-if="isEstadoPago('DEVUELTO')"
                type="warning"
                message="El comprobante no puede marcarse como usado ya que el pago ha sido devuelto."
              />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import { useService } from '@colescba/app-vue'
import { useSession, useAcreditacionServicio } from '@colescba/ui'

import { useAccessControl } from '@/composables'

import Cargando from '@/components/Core/Cargando'
import Empty from '@/components/Core/Empty'
import MessageCard from '@/components/Core/MessageCard'
import MarcarUsoComprobante from '@/components/UsoComprobantes/MarcarUsoComprobante'
import InfoComprobanteUsado from '@/components/UsoComprobantes/InfoComprobanteUsado'

export default {
  name: 'UsarComprobante',

  mixins: [useService('portalPagos'), useSession(), useAcreditacionServicio(), useAccessControl()],

  components: { Cargando, Empty, MessageCard, MarcarUsoComprobante, InfoComprobanteUsado },

  data() {
    return {
      isLoading: false,
      numeroComprobante: null,
      comprobanteTerceros: null,
      marcarUsado: null,
      error: null,
    }
  },

  async mounted() {
    this.fetchEstadosUso({ cuitCuil: this.user.cuitCuil.numero, portalPagos: this.portalPagos })
    this.fetchEstadosPago({ cuitCuil: this.user.cuitCuil.numero, portalPagos: this.portalPagos })
    if (!this.sede) {
      await this.addSedeCajero({
        cuitCuil: this.user.cuitCuil.numero,
        portalPagos: this.portalPagos,
        oficinaId: this.getOficinaUsuario.oficinaId,
      })
    }
  },

  computed: {
    ...mapGetters({
      sede: 'app/sedeCajero',
    }),
    ...mapState({
      estadosUso: state => state.app.estadosUso,
      estadosPago: state => state.app.estadosPago,
    }),
    comprobante() {
      return this.comprobanteTerceros?.comprobante
    },
    comprobanteUsado() {
      return this.comprobanteTerceros?.uso.estado_id == 'USADO'
    },
    cantEstadosAnulados() {
      return this.comprobanteTerceros?.log_uso.filter(e => e.estado_id == 'USO_ANULADO').length
    },
    mismaSede() {
      return this.sede.id.trim() == this.comprobanteTerceros.sede.id.trim()
    },
    getOficinaUsuario() {
      return this.rolCajero.oficinas[0]
    },
    rolCajero() {
      return this.rolesAcreditados.find(rol => rol.codigo === 'CAJERO')
    },
  },

  methods: {
    ...mapActions({
      addSedeCajero: 'app/addSedeCajero',
      fetchEstadosUso: 'app/fetchEstadosUso',
      fetchEstadosPago: 'app/fetchEstadosPago',
    }),
    async buscarComprobante() {
      this.isLoading = true
      this.error = null
      try {
        this.comprobanteTerceros = await this.portalPagos.getComprobanteTerceros(
          this.user.cuitCuil.numero,
          this.numeroComprobante
        )
      } catch (error) {
        this.error = true
      } finally {
        this.isLoading = false
      }
    },
    textoEstadoUso(estadoId) {
      return this.estadosUso?.find(e => e.id.trim() == estadoId).descripcion
    },
    textoEstadoPago(estadoId) {
      return this.estadosPago?.find(e => e.id.trim() == estadoId).descripcion
    },
    isEstadoPago(estadoId) {
      return this.comprobanteTerceros.pago.estado_id == estadoId
    },
  },
}
</script>
